import { useContext, useState } from "react";
import dayjs from "dayjs";
import { TagsContext } from "../firebaseio/TagsContext";
import { UserContext } from "../firebaseio/UserContext";
import {
  firestoreCreateTag,
  firestoreDeleteTagAndRemoveFromTransactions,
  firestoreUpdateTag,
  firestoreUpdateTagsOrder,
} from "../firebaseio/firestoreIo";
import { RyeButton } from "../rye/RyeButton";
import { RyeDraggableList } from "../rye/RyeDraggableList";
import { RyeMainContentPanel } from "../rye/RyeMainContentPanel";
import { RyeEmptyState } from "../rye/RyeEmptyState";
import { RyeSkeletonList } from "../rye/RyeSkeleton";
import { LoadingInitialDataContext } from "../LoadUser";
import { buildClasses } from "../utils/buildClasses";
import { getTagColor, pickNextTagColorId } from "./tagColors";
import { TagColorPicker } from "./TagColorPicker";

export function TagsScreen(): JSX.Element {
  const user = useContext(UserContext);
  const tags = useContext(TagsContext);
  const loading = useContext(LoadingInitialDataContext);
  const [newTagName, setNewTagName] = useState<string>("");

  const sortedTags = Array.from(tags.values()).sort(
    (a, b) => a.order_position - b.order_position,
  );

  async function handleCreateTag() {
    const name = newTagName.trim();
    if (name.length === 0) return;
    const exists = sortedTags.some(
      (t) => t.name.toLowerCase() === name.toLowerCase(),
    );
    if (exists) return;
    const minOrder = sortedTags.reduce(
      (acc, t) => Math.min(acc, t.order_position),
      0,
    );
    await firestoreCreateTag(user.uid, {
      name,
      color: pickNextTagColorId(sortedTags.length),
      order_position: minOrder - 1,
      created_timestamp_secs: dayjs().unix(),
    });
    setNewTagName("");
  }

  const tagItems = sortedTags.map((tag) => {
    const color = getTagColor(tag.color);
    return {
      id: tag.id,
      value: (
        <div
          key={tag.id}
          className={buildClasses(
            "flex",
            "items-center",
            "justify-between",
            "w-full",
            "py-2",
            "px-3",
            "group/tagitem",
          )}
        >
          <div className={buildClasses("flex", "items-center", "gap-2")}>
            <TagColorPicker
              currentColorId={tag.color}
              onSelect={(colorId) =>
                firestoreUpdateTag(user.uid, tag.id, { color: colorId })
              }
            />
            <span
              className={buildClasses("px-2", "py-0.5", "rounded", "text-sm")}
              style={{ backgroundColor: color.bg, color: color.text }}
            >
              {tag.name}
            </span>
          </div>
          <div
            className={buildClasses("invisible", "group-hover/tagitem:visible")}
          >
            <RyeButton
              icon="delete"
              variant="transparent"
              vibe="subdued"
              size="sm"
              onClick={() =>
                firestoreDeleteTagAndRemoveFromTransactions(user.uid, tag.id)
              }
            />
          </div>
        </div>
      ),
    };
  });

  // The field and button that add a tag, in the empty state's card or above
  // the list.
  const newTagForm = (
    <div className={buildClasses("flex", "items-center", "gap-2")}>
      <input
        type="text"
        value={newTagName}
        placeholder="New tag name"
        onChange={(e) => setNewTagName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleCreateTag();
        }}
        className={buildClasses(
          "px-3",
          "h-9",
          "bg-surface",
          "border",
          "border-on-surface-200",
          "rounded-md",
          "outline-none",
          "focus:border-purple-500",
          "text-on-surface-900",
          "flex-1",
          "max-w-xs",
        )}
      />
      <RyeButton
        text="Add tag"
        icon="add"
        variant="outlined"
        size="sm"
        onClick={handleCreateTag}
      />
    </div>
  );

  return (
    <RyeMainContentPanel title="Tags" roundedTl={false} roundedBl={false}>
      {loading.tags ? (
        <RyeSkeletonList />
      ) : sortedTags.length === 0 ? (
        <RyeEmptyState
          size="compact"
          icon="sell"
          title="Create some tags"
          description="In addition to categories, tags are another way to label your transactions, and (unlike categories) a single transaction can have more than one tag."
          note="Tags are great for tracking one-off groupings, like all of the expenses for a specific vacation or event. Note, you can easily create new tags on the transactions screen as well."
          actions={newTagForm}
        />
      ) : (
        <>
          <div className={buildClasses("pb-6", "text-sm")}>
            Tags let you label transactions with custom keywords. Drag to
            reorder. Deleting a tag removes it from any transactions that have
            it.
          </div>
          <div className={buildClasses("pb-6")}>{newTagForm}</div>
          <RyeDraggableList
            inputItems={tagItems}
            variant={"dividing-lines"}
            onOrderUpdate={(newIdList) =>
              firestoreUpdateTagsOrder(user.uid, newIdList)
            }
          />
        </>
      )}
    </RyeMainContentPanel>
  );
}
